// Single home for the raw-fetch AWS Cognito Identity Provider calls the login
// flow needs (InitiateAuth / RespondToAuthChallenge), plus the method→response-key
// map and the ChallengeParameters helpers.
//
// This extracts the byte-identical raw-fetch pattern that used to live inline in
// AuthContext.tsx (same endpoint host, same `application/x-amz-json-1.1`
// content-type, same `X-Amz-Target` header, same region/clientId sourcing, same
// USER_PASSWORD_AUTH flow) so the challenge-generic login (MFA plan Phase 1 / D6)
// has one tested surface to build on. No behaviour change vs. the previous inline
// fetches — only relocation + typing.

import { MfaChallengeType, MfaMethod, MfaResponseKey } from '../types/enums'

/** The subset of INNOVATEFLOW_CONFIG the Cognito IDP calls require. */
export interface CognitoClientConfig {
  REGION: string
  COGNITO_CLIENT_ID: string
}

export interface CognitoAuthenticationResult {
  IdToken: string
  AccessToken: string
  RefreshToken?: string
  ExpiresIn?: number
  TokenType?: string
}

/** Shape of the JSON body returned by InitiateAuth / RespondToAuthChallenge /
 * AssociateSoftwareToken / VerifySoftwareToken. */
export interface CognitoAuthResponse {
  AuthenticationResult?: CognitoAuthenticationResult
  ChallengeName?: string
  Session?: string
  ChallengeParameters?: Record<string, string>
  // AssociateSoftwareToken response (MFA_SETUP enrolment, Story #4709).
  SecretCode?: string
  // VerifySoftwareToken response.
  Status?: string
  // Present on Cognito error responses (e.g. CodeMismatchException).
  __type?: string
  message?: string
}

const IDP_TARGET_PREFIX = 'AWSCognitoIdentityProviderService'

function cognitoIdpUrl(region: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/`
}

async function callCognitoIdp(
  config: CognitoClientConfig,
  action:
    | 'InitiateAuth'
    | 'RespondToAuthChallenge'
    | 'RevokeToken'
    | 'AssociateSoftwareToken'
    | 'VerifySoftwareToken'
    | 'ForgotPassword'
    | 'ConfirmForgotPassword',
  payload: Record<string, unknown>,
): Promise<CognitoAuthResponse> {
  const response = await fetch(cognitoIdpUrl(config.REGION), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `${IDP_TARGET_PREFIX}.${action}`,
    },
    body: JSON.stringify(payload),
  })
  return response.json() as Promise<CognitoAuthResponse>
}

/**
 * RevokeToken — Story #4684 Task #4697 (WS-12, Bug #2609/#2631).
 *
 * Invalidates a refresh token AND every access/id token that was ever issued
 * from it, server-side, immediately. This requires `enable_token_revocation
 * = true` on the Cognito app client, which the InnovateFlow web client
 * already has (terraform/layers/07k-innovateflow/modules/innovateflow-auth/
 * main.tf) — without this call, a token captured before logout (e.g. a stolen
 * access token, or a browser-history/XSS leak) would keep working until it
 * naturally expired (now up to 1h, per the same terraform config), even
 * though the user believes they signed out. Call this BEFORE clearing the
 * refresh token from storage on every logout.
 *
 * Same raw-fetch pattern as initiateAuth/respondToAuthChallenge above — no
 * AWS SDK, no client secret (this is a public SPA client).
 */
export function revokeToken(
  config: CognitoClientConfig,
  refreshToken: string,
): Promise<CognitoAuthResponse> {
  return callCognitoIdp(config, 'RevokeToken', {
    Token: refreshToken,
    ClientId: config.COGNITO_CLIENT_ID,
  })
}

/**
 * ForgotPassword — Story #6712 (UX-004). Public (unauthenticated) call: Cognito
 * emails a verification code to the address on the account. Same raw-fetch
 * pattern as the other calls here; no client secret (public SPA client).
 */
export function forgotPassword(
  config: CognitoClientConfig,
  username: string,
): Promise<CognitoAuthResponse> {
  return callCognitoIdp(config, 'ForgotPassword', {
    ClientId: config.COGNITO_CLIENT_ID,
    Username: username,
  })
}

/** ConfirmForgotPassword — completes the reset with the emailed code + new password. */
export function confirmForgotPassword(
  config: CognitoClientConfig,
  username: string,
  code: string,
  newPassword: string,
): Promise<CognitoAuthResponse> {
  return callCognitoIdp(config, 'ConfirmForgotPassword', {
    ClientId: config.COGNITO_CLIENT_ID,
    Username: username,
    ConfirmationCode: code,
    Password: newPassword,
  })
}

/** InitiateAuth with the USER_PASSWORD_AUTH flow (the only flow this SPA uses). */
export function initiateAuth(
  config: CognitoClientConfig,
  username: string,
  password: string,
): Promise<CognitoAuthResponse> {
  return callCognitoIdp(config, 'InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: config.COGNITO_CLIENT_ID,
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password,
    },
  })
}

/** RespondToAuthChallenge for any challenge (MFA code, SELECT_MFA_TYPE, NEW_PASSWORD_REQUIRED). */
export function respondToAuthChallenge(
  config: CognitoClientConfig,
  challengeName: string,
  session: string,
  responses: Record<string, string>,
): Promise<CognitoAuthResponse> {
  return callCognitoIdp(config, 'RespondToAuthChallenge', {
    ClientId: config.COGNITO_CLIENT_ID,
    ChallengeName: challengeName,
    Session: session,
    ChallengeResponses: responses,
  })
}

/**
 * AssociateSoftwareToken keyed by the pending-login Session (Story #4709 —
 * MFA_SETUP enrolment). Deliberately Session-based, never AccessToken-based:
 * at this point in the login flow Cognito has not issued any tokens (that's
 * the whole reason MFA_SETUP exists — the user isn't authenticated yet), so
 * the AccessToken-keyed variant the authenticated Settings flow uses
 * (services/api.ts associateMfa()) cannot apply here.
 */
export function associateSoftwareTokenWithSession(
  config: CognitoClientConfig,
  session: string,
): Promise<CognitoAuthResponse> {
  return callCognitoIdp(config, 'AssociateSoftwareToken', { Session: session })
}

/**
 * VerifySoftwareToken keyed by the Session AssociateSoftwareToken returned.
 * A `Status: 'SUCCESS'` response proves the code matched but does NOT itself
 * grant tokens — the caller must still answer the MFA_SETUP challenge with
 * the Session this returns via respondToAuthChallenge() to complete sign-in.
 */
export function verifySoftwareTokenWithSession(
  config: CognitoClientConfig,
  session: string,
  userCode: string,
  friendlyDeviceName?: string,
): Promise<CognitoAuthResponse> {
  const payload: Record<string, unknown> = { Session: session, UserCode: userCode }
  if (friendlyDeviceName) payload.FriendlyDeviceName = friendlyDeviceName
  return callCognitoIdp(config, 'VerifySoftwareToken', payload)
}

/**
 * Client-side otpauth:// URI for the QR code, byte-identical in shape to the
 * authenticated Settings enrolment path (lambda/innovateflow-api/users/handler.py
 * _mfa_associate) — same issuer/label — so the QR/manual-key screen looks the
 * same whether enrolment happens at login (no access token yet) or from Settings.
 */
export function buildTotpOtpauthUri(email: string, secretCode: string): string {
  return `otpauth://totp/InnovateFlow:${email}?secret=${secretCode}&issuer=InnovateFlow`
}

/** Group a base32 TOTP secret into readable 4-character blocks for display. */
export function groupTotpSecret(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
}

/**
 * Map an MFA method token → the ChallengeResponses key that carries its code.
 * Cognito requires the method-specific key (a TOTP code sent as SMS_MFA_CODE is
 * rejected), so this is the authoritative mapping the login responder uses.
 */
export function mfaMethodToChallengeResponseKey(method: string): string {
  switch (method) {
    case MfaMethod.SOFTWARE_TOKEN_MFA:
      return MfaResponseKey.SOFTWARE_TOKEN_MFA_CODE
    case MfaMethod.SMS_MFA:
      return MfaResponseKey.SMS_MFA_CODE
    case MfaMethod.EMAIL_OTP:
      return MfaResponseKey.EMAIL_OTP_CODE
    default:
      throw new Error(`Unsupported MFA method: ${method}`)
  }
}

/** True when the challenge name is a direct, code-entry second-factor challenge. */
export function isCodeChallenge(challengeName: string | undefined): boolean {
  return (
    challengeName === MfaChallengeType.SOFTWARE_TOKEN_MFA ||
    challengeName === MfaChallengeType.SMS_MFA ||
    challengeName === MfaChallengeType.EMAIL_OTP
  )
}

/**
 * Parse the MFAS_CAN_CHOOSE ChallengeParameter, which Cognito returns as a
 * JSON-encoded string array (e.g. '["SMS_MFA","SOFTWARE_TOKEN_MFA"]'). Returns
 * an empty array when absent or malformed — never throws.
 */
export function parseMfasCanChoose(raw: string | undefined): string[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((m): m is string => typeof m === 'string') : []
  } catch {
    return []
  }
}

/** Human-readable noun for a method token — used in the challenge screen's method-aware copy. */
export function mfaMethodLabel(method: string | undefined): string {
  switch (method) {
    case MfaMethod.SOFTWARE_TOKEN_MFA:
      return 'authenticator app'
    case MfaMethod.SMS_MFA:
      return 'text message'
    case MfaMethod.EMAIL_OTP:
      return 'email'
    default:
      return 'verification'
  }
}
